import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Button,
    Modal,
    Nav,
    Spinner,
    Toast,
    ToastContainer,
} from 'react-bootstrap';

import { ReportsData } from '../interface';
import { dataToCsv } from '../csvUtils';
import { globalURLS } from '../../../Global/urls.js';
import { useReportContext } from '../../../Context/index';

import './AudienceCheckModal.css';

export interface AudienceSpecialtyRow {
    specialtyid: string;
    name: string;
    usercount: number;
}

interface Props {
    show: boolean;
    report: ReportsData | null;
    onHide: () => void;
}

function normalizeSpecialtyRows(data: unknown): AudienceSpecialtyRow[] {
    if (!Array.isArray(data)) return [];
    const rows: AudienceSpecialtyRow[] = [];
    for (const raw of data) {
        if (!raw || typeof raw !== 'object') continue;
        const r = raw as Record<string, unknown>;
        const id =
            r.specialtyid ??
            r.specialtyId ??
            r.SpecialtyId ??
            r.specialty_id;
        const name = r.name ?? r.Name ?? '';
        const uc =
            r.usercount ?? r.UserCount ?? r.count ?? r.Count ?? 0;
        if (typeof id !== 'string' || !id) continue;
        const n =
            typeof uc === 'number' ? uc : Number(String(uc)) || 0;
        rows.push({
            specialtyid: id,
            name: String(name),
            usercount: n,
        });
    }
    return rows;
}

const AudienceCheckModal: React.FC<Props> = ({ show, report, onHide }) => {
    const { setErrorToastShow } = useReportContext();
    const [rows, setRows] = useState<AudienceSpecialtyRow[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingList, setLoadingList] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [showDownloadOk, setShowDownloadOk] = useState(false);

    const downloadEndPoint =
        report?.audienceDownloadEndPoint ?? 'ADaud_spec_count2';
    const listEndPoint = report?.endPoint ?? 'ADaud_spec_count1';

    const fetchList = useCallback(async () => {
        if (!show || !report) return;
        setLoadingList(true);
        try {
            const resp = await axios.post(
                `${globalURLS.baseURL}${listEndPoint}`,
                {},
                globalURLS.config,
            );
            if (resp?.data?.success === true && resp?.data?.data !== undefined) {
                setRows(normalizeSpecialtyRows(resp.data.data));
                setSelectedIds(new Set());
                setSearchTerm('');
            } else {
                setRows([]);
                setErrorToastShow(true);
            }
        } catch {
            setRows([]);
            setErrorToastShow(true);
        } finally {
            setLoadingList(false);
        }
    }, [show, report, listEndPoint, setErrorToastShow]);

    useEffect(() => {
        if (show && report) {
            fetchList();
        }
    }, [show, report, fetchList]);

    const toggleOne = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const filteredRows = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(row =>
            row.name.toLowerCase().includes(q),
        );
    }, [rows, searchTerm]);

    const allSelected =
        filteredRows.length > 0 &&
        filteredRows.every(r => selectedIds.has(r.specialtyid));
    const toggleAll = () => {
        if (allSelected) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                filteredRows.forEach(r => next.delete(r.specialtyid));
                return next;
            });
        } else {
            setSelectedIds(prev => {
                const next = new Set(prev);
                filteredRows.forEach(r => next.add(r.specialtyid));
                return next;
            });
        }
    };

    const totalAudience = useMemo(
        () =>
            rows.reduce(
                (sum, row) =>
                    selectedIds.has(row.specialtyid)
                        ? sum + row.usercount
                        : sum,
                0,
            ),
        [rows, selectedIds],
    );

    const handleClose = () => {
        setShowDownloadOk(false);
        onHide();
    };

    const handleDownload = async () => {
        if (selectedIds.size === 0 || !report) return;
        setDownloading(true);
        try {
            const resp = await axios.post(
                `${globalURLS.baseURL}${downloadEndPoint}`,
                { specialtyids: Array.from(selectedIds) },
                globalURLS.config,
            );
            if (resp?.data?.success === true && resp?.data?.data !== undefined) {
                const data = resp.data.data;
                const csv = dataToCsv(data);
                const blob = new Blob(['\uFEFF' + csv], {
                    type: 'text/csv;charset=utf-8',
                });
                const objectUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = `${downloadEndPoint}_audience.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
                setShowDownloadOk(true);
            } else {
                setErrorToastShow(true);
            }
        } catch {
            setErrorToastShow(true);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <>
            <ToastContainer
                position="top-end"
                className="p-3"
                style={{ zIndex: 1080 }}
            >
                <Toast
                    bg="success"
                    show={showDownloadOk}
                    onClose={() => setShowDownloadOk(false)}
                    delay={3000}
                    autohide
                >
                    <Toast.Header closeButton>
                        <strong className="me-auto">Download complete</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">
                        CSV saved to your downloads folder.
                    </Toast.Body>
                </Toast>
            </ToastContainer>
            <Modal
                show={show}
                onHide={handleClose}
                animation={false}
                centered
                dialogClassName="audience-check-modal-dialog"
                contentClassName="audience-check-modal-content d-flex flex-column"
            >
                <Modal.Header closeButton className="flex-shrink-0">
                    <Modal.Title className="mb-0">
                        {report?.name ?? 'Audience check'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="audience-check-modal-body">
                    <Nav
                        variant="tabs"
                        className="audience-check-nav mb-0"
                        defaultActiveKey="specialty"
                    >
                        <Nav.Item>
                            <Nav.Link eventKey="specialty" active>
                                Specialty
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <div className="my-3">
                        <input
                            type="search"
                            className="form-control"
                            placeholder="Search specialty"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            aria-label="Search specialty"
                        />
                    </div>
                    {loadingList ? (
                        <div className="d-flex justify-content-center align-items-center py-5 flex-grow-1">
                            <Spinner animation="border" role="status" />
                        </div>
                    ) : (
                        <div className="audience-check-table-wrap">
                            <table className="table table-bordered table-hover table-sm audience-check-table mb-0">
                                <colgroup>
                                    <col className="col-check" />
                                    <col />
                                    <col className="col-count" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th
                                            className="col-check text-center align-middle"
                                            scope="col"
                                        >
                                            <input
                                                type="checkbox"
                                                className="form-check-input audience-check-input"
                                                checked={allSelected}
                                                onChange={toggleAll}
                                                aria-label="Select all specialties"
                                                disabled={rows.length === 0}
                                            />
                                        </th>
                                        <th
                                            className="align-middle"
                                            scope="col"
                                        >
                                            Name
                                        </th>
                                        <th
                                            className="col-count align-middle text-end"
                                            scope="col"
                                        >
                                            Count
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map(row => (
                                        <tr key={row.specialtyid}>
                                            <td className="col-check text-center align-middle">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input audience-check-input"
                                                    checked={selectedIds.has(
                                                        row.specialtyid,
                                                    )}
                                                    onChange={() =>
                                                        toggleOne(
                                                            row.specialtyid,
                                                        )
                                                    }
                                                    aria-label={`Select ${row.name}`}
                                                />
                                            </td>
                                            <td className="align-middle">
                                                {row.name}
                                            </td>
                                            <td className="col-count align-middle text-end">
                                                {row.usercount}
                                            </td>
                                        </tr>
                                    ))}
                                    {!loadingList && filteredRows.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={3}
                                                className="text-center text-muted py-3"
                                            >
                                                No specialties found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="audience-check-modal-footer flex-shrink-0">
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                    <span className="text-muted me-2">
                        Total Audience: {totalAudience}
                    </span>
                    <Button
                        variant="success"
                        disabled={
                            downloading ||
                            selectedIds.size === 0 ||
                            loadingList
                        }
                        onClick={handleDownload}
                    >
                        {downloading ? (
                            <>
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                />
                                Downloading…
                            </>
                        ) : (
                            'Download CSV'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AudienceCheckModal;
